import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Platform } from '@ionic/angular';
import {
  ChartDataset,
  ChartTypeRegistry,
  TooltipCallbacks,
  TooltipItem
} from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { AnleggMaaned } from 'src/app/services/forbruk/forbruk.types';
import { monthsShort } from 'src/app/utils/date-utils';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from '../../forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';

@Component({
  selector: 'app-month-chart-usage',
  templateUrl: './month-chart-usage.component.html',
  styleUrls: ['./month-chart-usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthChartUsageComponent implements OnInit, OnChanges {
  @Input() valgtAnlegg: AnleggMaaned;
  @Input() selectedYears: number[];

  useCurrentYear: boolean;
  usePreviousYear: boolean;
  dataSets: ChartDataset<'bar'>[];
  labels: string[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar'>>;
  isBrowser: boolean;
  tooltipFilter: (item: TooltipItem<keyof ChartTypeRegistry>) => boolean;

  constructor(private platform: Platform) {}

  ngOnInit(): void {
    this.isBrowser =
      !this.platform.is('capacitor') &&
      !this.platform.is('mobileweb') &&
      this.platform.width() >= 1000;

    if (this.valgtAnlegg && this.selectedYears) {
      this.getChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.valgtAnlegg &&
      this.selectedYears &&
      hasChanged([
        changes?.valgtAnlegg,
        changes?.selectedDate,
        changes?.selectedYears
      ])
    ) {
      this.getChartData();
    }
  }

  getChartData() {
    this.dataSets = [];
    this.labels = monthsShort;
    this.title = 'Strømforbruk';
    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'kWh'),
      title: (tt) =>
        titleTooltipCallback(tt, 'month', 'kWh', 'forbruk_tooltip_chart1')
    };

    this.tooltipFilter = (item) => {
      return item.raw !== 0;
    };

    const colorsForbruk = ['#002987', '#0a66ff', '#26b41b'];
    const colorsProd = ['#ff6c00', '#ff80ff', '#004c39'];

    // Kopier lista for å unngå å mutere original liste
    [...this.selectedYears].sort().map((year, i) => {
      const values = this.valgtAnlegg.maanedVerdiList.filter(
        (x) => x.aar === year
      );

      if (values.length > 0) {
        const forbruk = [...Array(12)].map(
          (_, i) => values.find((x) => x.mndNr - 1 === i)?.verdi || 0
        );

        this.dataSets.push({
          data: forbruk,
          backgroundColor: colorsForbruk[i],
          label: 'Forbruk ' + year.toString(),
          pointStyle: 'circle',
          barThickness: 8
        });

        // Plusskunde strømproduksjon
        let prodThisYear = 0;
        if (this.valgtAnlegg.plusskunde) {
          const produksjon = [...Array(12)].map(
            (_, i) => values.find((x) => x.mndNr - 1 === i)?.plussverdi || 0
          );
          prodThisYear = produksjon.reduce(
            (summed, current) => summed + current,
            0
          );

          this.dataSets.push({
            pointStyle: 'circle',
            label: 'Overskudd ' + year.toString(),
            data: produksjon,
            backgroundColor: colorsProd[i],
            barThickness: 8
          });
        }
      }
    });

    if (this.isBrowser) {
      if (this.dataSets.length === 1) {
        this.dataSets = this.dataSets.map((x) => ({ ...x, barThickness: 16 }));
      }
    } else {
      if (this.dataSets.length > 5) {
        this.dataSets = this.dataSets.map((x) => ({ ...x, barThickness: 4 }));
      } else if (this.dataSets.length > 2) {
        this.dataSets = this.dataSets.map((x) => ({ ...x, barThickness: 6 }));
      }
    }
  }
}
